// List every scoped Promotion Idea once, with draft and total-variation counts.
// Uses two scoped reads and opens the existing Idea drafting workspace.
// Pagination includes undrafted and legacy Ideas; stale route results are ignored.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SeoHead from "../../../components/seo_head";
import LGVNotificationBanner from "../../../components/lgv/lgv_notification_banner";
import PromotionActionNavigation from "../../../promotion/components/promotion_action_navigation";
import PromotionPageHeader from "../../../promotion/components/promotion_page_header";
import Ideas from "../../../promotion/components/ideas";
import { getPromotionIdeaDashboard } from "../../../promotion/services/promotion_workflow_service";
import { updatePromotionIdeaStatus } from "../../../promotion/services/promotion_idea_service";
import { PROMOTION_IDEA_STATUS } from "../../../promotion/constants/promotion_status";
import { useEventContext } from "../../../hooks/use_event_context";

const PAGE_SIZE = 25;

const PromotionDashboard = () => {
  const { eventslug } = useParams();
  const { eventId, eventLoading } = useEventContext({ redirectIfNotFound: false });
  const dataReady = !eventslug || Boolean(eventId);
  const scopeKey = `${eventslug || "internal"}:${eventId || ""}`;
  const promotionUrl = (page) => eventslug ? `/${eventslug}/${page}` : `/${page}`;
  const [result, setResult] = useState({ scopeKey: "", rows: [], loading: true, error: "" });
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: "error", message: "" });

  useEffect(() => {
    let cancelled = false;
    setPage(0);
    setNotification({ show: false, type: "error", message: "" });
    setResult({ scopeKey, rows: [], loading: true, error: "" });
    if (!dataReady || eventLoading) return undefined;
    getPromotionIdeaDashboard({ eventId: eventId || null }).then((rows) => {
      if (!cancelled) setResult({ scopeKey, rows, loading: false, error: "" });
    }).catch((error) => {
      if (cancelled) return;
      const message = error.message || "Unable to load Promotion Ideas.";
      setResult({ scopeKey, rows: [], loading: false, error: message });
      setNotification({ show: true, type: "error", message });
    });
    return () => { cancelled = true; };
  }, [dataReady, eventId, eventLoading, scopeKey, revision]);

  useEffect(() => {
    if (!notification.show) return undefined;
    const timer = setTimeout(() => setNotification((previous) => ({ ...previous, show: false })), 20000);
    return () => clearTimeout(timer);
  }, [notification]);

  const sameScope = result.scopeKey === scopeKey;
  const rows = sameScope ? result.rows : [];
  const loading = !sameScope || !dataReady || eventLoading || result.loading;
  const error = sameScope ? result.error : "";
  const visibleRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const postsByIdeaAndPlatform = {};
  rows.forEach((idea) => Object.entries(idea.postsByPlatform || {}).forEach(([platform, post]) => {
    postsByIdeaAndPlatform[`${idea.id}:${platform}`] = post;
  }));
  const archiveIdea = async (idea) => {
    if (saving || !window.confirm(`Archive "${idea.title}"?`)) return;
    const identity = scopeKey;
    setSaving(true);
    try {
      await updatePromotionIdeaStatus({ ideaId: idea.id, ideaStatus: PROMOTION_IDEA_STATUS.ARCHIVED });
      setResult((previous) => previous.scopeKey === identity ? { ...previous, rows: previous.rows.map((row) => row.id === idea.id ? { ...row, ideaStatus: PROMOTION_IDEA_STATUS.ARCHIVED } : row) } : previous);
      setNotification({ show: true, type: "success", message: "Promotion idea archived.", scopeKey: identity });
    } catch (error) {
      setNotification({ show: true, type: "error", message: error.message || "Unable to archive promotion idea.", scopeKey: identity });
    } finally { setSaving(false); }
  };

  return <>
    <SeoHead title="Promotion Engine | Let's Go Vote" description="Promotion Ideas and their social drafts." noindex />
    <div className="container-fluid py-4"><div className="container" style={{ maxWidth: "1500px" }}>
      <PromotionPageHeader title="Promotion Engine" subtitle="Open an Idea to create and manage its social drafts." />
      <PromotionActionNavigation current="dashboard" promotionUrl={promotionUrl} />
      <LGVNotificationBanner {...notification} show={sameScope && notification.show && (!notification.scopeKey || notification.scopeKey === scopeKey)}
        onClose={() => setNotification((previous) => ({ ...previous, show: false }))} />
      <Ideas ideas={visibleRows} totalCount={rows.length} promotionUrl={promotionUrl}
        postsByIdeaAndPlatform={postsByIdeaAndPlatform} loading={loading} saving={saving} onArchive={archiveIdea}
        error={error} onRetry={() => setRevision((previous) => previous + 1)}
        emptyMessage="No ideas yet. Create an Idea or start a promotion from an event page.">
          {rows.length > PAGE_SIZE && <div className="d-flex justify-content-between align-items-center mt-3">
            <button type="button" className="btn btn-sm btn-outline-primary" disabled={page === 0} onClick={() => setPage((previous) => previous - 1)}>Previous</button>
            <span className="small text-muted">Page {page + 1} of {Math.ceil(rows.length / PAGE_SIZE)}</span>
            <button type="button" className="btn btn-sm btn-outline-primary" disabled={(page + 1) * PAGE_SIZE >= rows.length} onClick={() => setPage((previous) => previous + 1)}>Next</button>
          </div>}
      </Ideas>
    </div></div>
  </>;
};

export default PromotionDashboard;
